// Route guard: redirects unauthenticated users to /login, preserves the
// originally requested URL via ?redirect=, and waits out the initial
// session-rehydration window so a returning user isn't bounced to login
// while the refresh-cookie exchange is still in flight.
//
// Conceptually a stripped-down mirror of Auware's ProtectedRoute.
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuthStore } from "@/stores/auth";

/**
 * Wrap any authenticated route group:
 *
 *   <Route element={<RequireAuth />}>
 *     <Route path="/" element={<Dashboard />} />
 *     ...
 *   </Route>
 */
export function RequireAuth(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const location = useLocation();

  // While the rehydration / refresh-cookie exchange is running, show a
  // bare spinner so the user doesn't see a redirect flash.
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-neutral-50">
        <svg
          className="h-8 w-8 animate-spin text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      </div>
    );
  }

  if (!user) {
    const redirectTo = `${location.pathname}${location.search}`;
    const search = redirectTo === "/" ? "" : `?redirect=${encodeURIComponent(redirectTo)}`;
    return <Navigate to={`/login${search}`} replace />;
  }

  return <Outlet />;
}

export default RequireAuth;
